// src/components/special/GlobalPhotoGuard.jsx
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PhotoRequestModal from "../events/PhotoRequestModal.jsx";

/**
 * GlobalPhotoGuard
 *
 * Глобальный сторож за фоткой:
 *  - сам НИЧЕГО не фетчит
 *  - получает user из App (там уже был вызов /accounts/profile/)
 *  - если user.photo_required_now === true и нет кастомного аватара —
 *    показывает PhotoRequestModal поверх всего приложения.
 *
 * Также есть небольшой "супресс" после успешной загрузки аватара,
 * чтобы модалка не переоткрывалась сразу же из-за того, что user
 * на фронте ещё не успел обновиться.
 */

// Время, в течение которого мы подавляем глобальный guard после аплоада
const SUPPRESS_MS = 8000; // 8 секунд; можно уменьшить до 5000 при желании

const GlobalPhotoGuard = ({ user }) => {
  const { t } = useTranslation("events");
  const [visible, setVisible] = useState(false);

  // timestamp (ms), до которого guard вообще ничего не показывает
  const suppressUntilRef = useRef(0);

  useEffect(() => {
    // --- Диагностика: смотрим, что реально приходит в guard ---
    // Открой Console в DevTools и посмотри эти логи.
    console.log("[GlobalPhotoGuard] user =", user);
    console.log("[GlobalPhotoGuard] photo_required_now =", user?.photo_required_now);
    console.log("[GlobalPhotoGuard] avatar_custom_url =", user?.avatar_custom_url);

    const now = Date.now();

    if (!user) {
      // Юзера нет — ничего не показываем
      setVisible(false);
      return;
    }

    // Если мы в окне супресса после успешного аплоада — тоже не показываем
    if (now < suppressUntilRef.current) {
      setVisible(false);
      return;
    }

    // Бэк уже посчитал, нужно ли требовать фото.
    // For moderated bad-photo reviews, an existing custom avatar must still be replaced.
    const reason = String(user.photo_requirement_reason || "");
    const needPhoto = Boolean(user.photo_required_now) && (
      reason === "bad_photo_review"
      || reason === "bad_photo_reports_threshold"
      || reason === "ai_photo_review"
      || !user.avatar_custom_url
    );

    setVisible(needPhoto);
  }, [user]);

  // Слушаем глобальное событие об обновлении аватара, чтобы сразу спрятать guard
  useEffect(() => {
    const handler = () => {
      // С момента аплоада даём SUPPRESS_MS, чтобы профиль успел обновиться
      suppressUntilRef.current = Date.now() + SUPPRESS_MS;
      setVisible(false);
    };

    window.addEventListener("vg-avatar-updated", handler);
    return () => window.removeEventListener("vg-avatar-updated", handler);
  }, []);

  const handleUploaded = () => {
    // После успешной загрузки фото прячем модалку в этой сессии
    suppressUntilRef.current = Date.now() + SUPPRESS_MS;
    setVisible(false);
  };

  const handleClose = () => {
    // Крестик: просто спрятали модалку сейчас.
    // Если бэк всё ещё возвращает photo_required_now = true и нет фотки,
    // guard позже снова поднимет модалку (после окончания SUPPRESS_MS).
    setVisible(false);
  };

  if (!visible) return null;

  const photoRequirementReason = String(user?.photo_requirement_reason || "");
  const isBadPhotoRequirement = photoRequirementReason === "bad_photo_review";
  const isBadPhotoThresholdRequirement = photoRequirementReason === "bad_photo_reports_threshold";
  const isAiPhotoRequirement = photoRequirementReason === "ai_photo_review";
  const isIncompleteRegistration = photoRequirementReason === "missing_registration_photo";
  const isBlockingRequirement = isBadPhotoRequirement
    || isBadPhotoThresholdRequirement
    || isAiPhotoRequirement
    || isIncompleteRegistration;

  return (
    <PhotoRequestModal
      visible={visible}
      onUploaded={handleUploaded}
      onClose={handleClose}
      allowClose={!isBlockingRequirement}
      titleOverride={
        isAiPhotoRequirement
          ? t("photo_request_modal.ai_photo_title")
          : isBadPhotoRequirement || isBadPhotoThresholdRequirement
          ? t("photo_request_modal.bad_photo_title")
          : ""
      }
      subtitleOverride={
        isAiPhotoRequirement
          ? t("photo_request_modal.ai_photo_subtitle")
          : isBadPhotoRequirement || isBadPhotoThresholdRequirement
          ? t(
              isBadPhotoThresholdRequirement
                ? "photo_request_modal.bad_photo_threshold_subtitle"
                : "photo_request_modal.bad_photo_subtitle"
            )
          : ""
      }
      // onSkipOnce не передаём: глобальный guard — про «поставь фотку».
    />
  );
};

export default GlobalPhotoGuard;
